import type { Cents } from "./money";
import type { FinancialBucket } from "./buckets";

/**
 * Mileage (Phase 5).
 *
 * Each entry stores its own FROZEN per-mile rate snapshot and calculated
 * amount so changing the rate table later never rewrites history. The
 * entry itself is the authoritative actual-cost source (it never creates a
 * Manual Actual Expense) and carries its Financial Bucket directly
 * (material pickup → Renovation; holding-period property check → Holding).
 *
 * Rates: an app-level effective-date table configured by the user. Berrier
 * Reno seeds NO rate and never presents one as an official tax/legal
 * figure — this is internal project-cost tracking.
 */
export interface MileageRate {
  id: string;
  /** ISO date the rate takes effect. */
  effectiveFrom: string;
  centsPerMile: Cents;
  note: string;
  createdAt: string;
}

export interface MileageEntry {
  id: string;
  projectId: string;
  date: string;
  purpose: string;
  startLocation: string;
  destination: string;
  /** Miles in tenths (31.4 mi -> 314). */
  milesX10: number;
  /** Frozen snapshot of the rate used. */
  rateCentsPerMile: Cents;
  /** round-half-up(milesX10 × rate ÷ 10). */
  amountCents: Cents;
  bucket: FinancialBucket;
  notes: string;
  voidedAt: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface MileageEntryInput {
  date: string;
  purpose: string;
  startLocation: string;
  destination: string;
  milesX10: number;
  rateCentsPerMile: Cents;
  bucket: FinancialBucket;
  notes: string;
}

/** Deterministic mileage amount: miles × rate, half-up to the cent. */
export function mileageAmountCents(milesX10: number, rateCentsPerMile: Cents): Cents {
  return Math.floor((milesX10 * rateCentsPerMile + 5) / 10);
}

export function validateMileageInput(input: MileageEntryInput): string[] {
  const problems: string[] = [];
  if (input.date.trim() === "") problems.push("Date is required.");
  if (input.purpose.trim() === "") problems.push("Purpose is required.");
  if (!Number.isSafeInteger(input.milesX10) || input.milesX10 <= 0) {
    problems.push("Miles must be a positive number.");
  }
  if (!Number.isSafeInteger(input.rateCentsPerMile) || input.rateCentsPerMile < 0) {
    problems.push("A per-mile rate is required (configure a default rate first).");
  }
  return problems;
}

/** The rate in effect on a date: latest effectiveFrom ≤ date, else null. */
export function rateForDate(rates: MileageRate[], date: string): MileageRate | null {
  const eligible = rates.filter((r) => r.effectiveFrom <= date).sort((a, b) =>
    a.effectiveFrom < b.effectiveFrom ? 1 : a.effectiveFrom > b.effectiveFrom ? -1 : 0,
  );
  return eligible[0] ?? null;
}
